import { reportUploadProgress } from "../utils/uploadProgress";

const DOCXEDITOR_API_HOST = import.meta.env.VITE_DOCXEDITOR_API_HOST;

/**
 * Converts an uploaded .docx file into Syncfusion's SFDT format via the
 * hosted DocumentEditor backend, so it can be opened in
 * DocumentEditorContainerComponent. Uses a bare request (not the app's `api`
 * axios client) since this hits a separate third-party service and must not
 * carry the app's Authorization header.
 */
export async function importDocxToSfdt(file: File): Promise<unknown> {
  if (!DOCXEDITOR_API_HOST) {
    throw new Error("Document editor service is not configured.");
  }

  const formData = new FormData();
  formData.append("file", file);

  // XHR rather than fetch: fetch has no upload progress, and a large
  // contract can take a while to reach the service. Bytes sent are reported
  // for this File, so a gallery tracking it shows a real progress bar.
  const { status, body } = await new Promise<{ status: number; body: string }>(
    (resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${DOCXEDITOR_API_HOST}/api/documenteditor/Import`);
      xhr.upload.onprogress = (e) =>
        reportUploadProgress(file, e.loaded, e.lengthComputable ? e.total : undefined);
      xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
      xhr.onerror = () => reject(new Error("Document import failed (network error)."));
      // Without this a down editor service leaves callers on a spinner forever.
      xhr.timeout = 60_000;
      xhr.ontimeout = () =>
        reject(new Error("The document editor service did not respond. Try again shortly."));
      xhr.send(formData);
    },
  );

  if (status < 200 || status >= 300) {
    throw new Error(`Document import failed (${status}). ${body}`.trim());
  }

  let data: { sfdt?: unknown; sections?: unknown } | null;
  try {
    data = JSON.parse(body);
  } catch {
    data = null;
  }
  if (!data || (!data.sfdt && !data.sections)) {
    throw new Error("Document editor service returned an invalid response.");
  }

  return data;
}
